'use client';

import { useId, useState } from 'react';
import type { CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiCheckboxCircleLine,
  RiGlobalLine,
  RiLockLine,
  RiShieldCheckLine,
  RiTimerLine,
  RiUserLine,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { dur, spring, springFade } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

import './autopilot.css';

/**
 * "Sınırları siz çizersiniz" — Otopilot'un korkuluğu.
 *
 * Sağda ayarlar ekranının ÖRNEK bir kopyası: değerler ürünün varsayılanları
 * (`@mentorivo/shared/platform-ops` → `policy.ts` DEFAULT_POLICY: adım başına
 * %25, günde 50 otomatik işlem, otomatik yayın kapalı / günde 3, geri
 * alınamaz kapalı, altı hassas kategori kapalı, onaysız kreatif kredisi 0).
 *
 * Tek etkileşimli satır adım sınırı: ziyaretçi kaydırdıkça altındaki örnek
 * ("Operatör bir bütçeyi %40 artırmak istiyor") "onaya düşer" ile "kendisi
 * uygular" arasında gidip geliyor. Bölümün anlattığı şey tam olarak bu —
 * sınır bir ayar değil, AI'ın hangi kararı tek başına verebileceği.
 *
 * Diğer satırlar gerçek anahtar DEĞİL, çizim: tıklanmıyor, ekran okuyucuya
 * değerleriyle düz metin olarak okunuyor ("Kapalı").
 *
 * Hareket: karar çipi değişirken bulanıktan netleşiyor; kaydırıcının izi
 * örnek eşiği (%40) sabit bir çentikle gösteriyor, eşiği geçtiğiniz an
 * çentik yeşile dönüyor (yalnız renk değil — çipin yazısı da değişiyor).
 */

const EXAMPLE = 40;
const SENSITIVE = ['access', 'destination', 'dataSharing', 'legal', 'publishing', 'funds'] as const;
const RULES = [
  { key: 'owner', Icon: RiUserLine },
  { key: 'self', Icon: RiLockLine },
  { key: 'personal', Icon: RiShieldCheckLine },
  { key: 'web', Icon: RiGlobalLine },
] as const;

const verdictV: Variants = {
  hidden: { opacity: 0, y: 4, filter: 'blur(3px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.snap) },
  exit: { opacity: 0, filter: 'blur(2px)', transition: { duration: dur.instant } },
};

export function AutopilotLimits() {
  const t = useTranslations('featuresAutopilot.limits');
  const uid = useId();
  const [step, setStep] = useState(25);
  const passes = step >= EXAMPLE;

  return (
    <ProductSection tone="gray" labelledBy={`${uid}-title`}>
      <div className="apl">
        <div className="apl-aside">
          <SectionHead
            id={`${uid}-title`}
            eyebrow={t('eyebrow')}
            title={t.rich('title', { hl })}
            lead={t('lead')}
          />

          <ul className="apl-rules">
            {RULES.map(({ key, Icon }) => (
              <li key={key} className="apl-rule">
                <Icon size={18} aria-hidden="true" />
                <div>
                  <p className="apl-rule-title">{t(`rules.${key}.title`)}</p>
                  <p className="apl-rule-text">{t(`rules.${key}.text`)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="apl-panel" role="group" aria-labelledby={`${uid}-panel`}>
          <div className="apl-panel-head">
            <p className="apl-panel-title" id={`${uid}-panel`}>
              {t('panel.title')}
            </p>
            <p className="apl-panel-mode">
              <span className="apl-mode">{t('panel.suggest')}</span>
              <span className="apl-mode apl-mode--on">{t('panel.autopilot')}</span>
            </p>
          </div>

          {/* --- Etkileşimli satır: adım sınırı --- */}
          <div className="apl-row apl-row--slider">
            <div className="apl-row-top">
              <label className="apl-label" htmlFor={`${uid}-step`}>
                {t('panel.step')}
              </label>
              <output className="apl-value" htmlFor={`${uid}-step`}>
                %{step}
              </output>
            </div>

            <div
              className="apl-range"
              style={
                {
                  /* Oran 0–1, değer değil: başparmağın merkezi izin iki
                     ucundan yarım başparmak içeride, yeşil dolgu ve çentik
                     aynı hesaptan (CSS'te `--apl-thumb` payıyla). */
                  '--apl-fill': (step - 1) / 99,
                  '--apl-mark': (EXAMPLE - 1) / 99,
                } as CSSProperties
              }
            >
              <input
                id={`${uid}-step`}
                className="apl-range-input"
                type="range"
                min={1}
                max={100}
                step={1}
                value={step}
                onChange={(event) => setStep(Number(event.target.value))}
                aria-describedby={`${uid}-verdict`}
              />
              <span
                className={`apl-range-mark${passes ? ' apl-range-mark--on' : ''}`}
                aria-hidden="true"
              />
            </div>

            <div className="apl-verdict" id={`${uid}-verdict`} aria-live="polite">
              <p className="apl-verdict-ask">{t('panel.example', { ask: EXAMPLE })}</p>
              <AnimatePresence mode="wait" initial={false}>
                <motion.p
                  key={passes ? 'yes' : 'no'}
                  className={`apl-verdict-chip${passes ? ' apl-verdict-chip--yes' : ''}`}
                  variants={verdictV}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  {passes ? (
                    <RiCheckboxCircleLine size={15} aria-hidden="true" />
                  ) : (
                    <RiTimerLine size={15} aria-hidden="true" />
                  )}
                  {t(passes ? 'panel.applies' : 'panel.asks')}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>

          {/* --- Çizim satırları: varsayılanlar --- */}
          <dl className="apl-defaults">
            <div className="apl-row">
              <dt className="apl-label">{t('panel.actions')}</dt>
              <dd className="apl-value">50</dd>
            </div>
            <div className="apl-row">
              <dt className="apl-label">
                {t('panel.launch')}
                <span className="apl-sub">{t('panel.launchNote')}</span>
              </dt>
              <dd className="apl-off">
                <span className="apl-switch" aria-hidden="true" />
                {t('panel.off')}
              </dd>
            </div>
            <div className="apl-row">
              <dt className="apl-label">{t('panel.irreversible')}</dt>
              <dd className="apl-off">
                <span className="apl-switch" aria-hidden="true" />
                {t('panel.off')}
              </dd>
            </div>
            <div className="apl-row">
              <dt className="apl-label">
                {t('panel.credits')}
                <span className="apl-sub">{t('panel.creditsNote')}</span>
              </dt>
              <dd className="apl-value">0</dd>
            </div>
            <div className="apl-row apl-row--chips">
              <dt className="apl-label">
                {t('panel.sensitive')}
                <span className="apl-sub">{t('panel.sensitiveNote')}</span>
              </dt>
              <dd>
                <ul className="apl-chips">
                  {SENSITIVE.map((key) => (
                    <li key={key} className="apl-chip">
                      <RiLockLine size={12} aria-hidden="true" />
                      {t(`panel.categories.${key}`)}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          </dl>

          <p className="apl-panel-foot">
            <RiLockLine size={14} aria-hidden="true" />
            {t('panel.foot')}
          </p>
        </div>
      </div>
    </ProductSection>
  );
}
